import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getFormattedOptionPaymentSys } from '../helpers';
import { INTERNAL_ACCOUNT_TYPE, PAYMENT_SYSTEM_TYPE } from '../consts';
import UserContext from '../../../../../../../../../../common/utils/getContext';
const Decimal = require('decimal.js');

export const useSelectedPaymentSystem = ({
    isActivePaymentSys,
    isComboPayment,
    setSelectedPaymentSystems,
    setSelectedPayment,
    setIsActivePaymentSys,
    selectedPaymentSystems,
    cartData
}) => {
    const { settings } = useContext(UserContext);

    const [selectedPaymentSys, setSelectedPaymentSys] = useState(null);
    const [currencyValue, setCurrencyValue] = useState(false);

    useEffect(() => {
        setSelectedPayment(selectedPaymentSys);
    }, [setSelectedPayment, selectedPaymentSys]);

    const handleClickOnPaymentSys = useCallback(
        (key, val) => {
            setSelectedPaymentSystems((prev) => {
                let newResult = [];
                if (key === INTERNAL_ACCOUNT_TYPE) {
                    if (isComboPayment) {
                        if (prev.find((item) => item.id === val.id)) {
                            return [...prev.filter((item) => item.id !== val.id)];
                        }

                        if (prev.length === 2) {
                            newResult = [...prev.filter((item) => item.keyType !== PAYMENT_SYSTEM_TYPE)];
                            setIsActivePaymentSys(false);
                            newResult = [...newResult, { ...val, keyType: key }];
                        } else {
                            newResult = [...prev, { ...val, keyType: key }];
                        }
                    } else {
                        newResult = [{ ...val, keyType: key, sumValue: cartData?.total_price }];
                    }
                }
                if (key === PAYMENT_SYSTEM_TYPE) {
                    const paymentSysToSelect = val || selectedPaymentSys;
                    if (!paymentSysToSelect) {
                        return [...prev];
                    }
                    if (isComboPayment && paymentSysToSelect) {
                        if (prev.find((item) => item.keyType === PAYMENT_SYSTEM_TYPE)) {
                            return [...prev.filter((item) => item.keyType !== PAYMENT_SYSTEM_TYPE)];
                        }

                        if (prev.length === 2) {
                            return [...prev];
                        }
                        newResult = [...prev, { ...paymentSysToSelect, keyType: key }];
                    }
                    if (!isComboPayment && paymentSysToSelect) {
                        return [{ ...paymentSysToSelect, sumValue: cartData?.total_price, keyType: PAYMENT_SYSTEM_TYPE }];
                    }
                }
                const newSelectedPaymentSystems = newResult?.map((item, index) => {
                    if (item.sumValue) {
                        return item;
                    }
                    if (
                        newResult[index ? 0 : 1]?.sumValue &&
                        newResult.length === 2 &&
                        Number(newResult[index ? 0 : 1]?.sumValue) > 0
                    ) {
                        const newSumValue = new Decimal(cartData?.total_price).minus(Number(newResult[index ? 0 : 1].sumValue));
                        return {
                            ...item,
                            sumValue: newSumValue.toString()
                        };
                    }

                    return item;
                });
                return newSelectedPaymentSystems;
            });
        },
        [cartData, setSelectedPaymentSystems, isComboPayment, setIsActivePaymentSys, selectedPaymentSys]
    );

    const handleClickCheckboxPaymentSys = useCallback(
        (e, forceSelectedPaymentSys) => {
            if (e) {
                e.stopPropagation();
            }
            setIsActivePaymentSys((prev) => {
                if (forceSelectedPaymentSys) {
                    handleClickOnPaymentSys(PAYMENT_SYSTEM_TYPE, forceSelectedPaymentSys);
                    return true;
                }
                if (!selectedPaymentSys) {
                    return false;
                }
                if (
                    selectedPaymentSystems?.length == 2 &&
                    !selectedPaymentSystems.some((item) => item.keyType === PAYMENT_SYSTEM_TYPE)
                ) {
                    return false;
                }

                if (!isComboPayment && prev) {
                    return prev;
                }
                handleClickOnPaymentSys(PAYMENT_SYSTEM_TYPE);
                return !prev;
            });
        },
        [handleClickOnPaymentSys, isComboPayment, selectedPaymentSys, selectedPaymentSystems, setIsActivePaymentSys]
    );

    const handleClickCheckboxInternalAcc = useCallback(
        (isDisabled, paymentSystem, handleClickOnPaymentSys) => {
            if (isDisabled) {
                return false;
            }
            if (!isComboPayment) {
                setIsActivePaymentSys(false);
            }
            handleClickOnPaymentSys(INTERNAL_ACCOUNT_TYPE, paymentSystem);
        },
        [isComboPayment, setIsActivePaymentSys]
    );

    const currentCurrenciesList = useMemo(
        () =>
            selectedPaymentSys?.currencies?.data?.length > 0
                ? selectedPaymentSys?.currencies?.data.map((item) => {
                      return getFormattedOptionPaymentSys(item, settings);
                  })
                : [],
        [selectedPaymentSys, settings]
    );

    const handleSetSelectedPaymentSys = useCallback(
        (key, val) => {
            let newSelectedPaymentSys = { ...val, keyType: PAYMENT_SYSTEM_TYPE };
            if (val?.currencies?.data?.length) {
                const defCurrency = getFormattedOptionPaymentSys(val?.currencies?.data[0], settings);
                newSelectedPaymentSys = { ...newSelectedPaymentSys, selectedCurrency: defCurrency };
                setCurrencyValue(defCurrency);
            }
            const partOfCondition = selectedPaymentSys && !isActivePaymentSys;

            if (!selectedPaymentSys || partOfCondition) {
                setSelectedPaymentSys(newSelectedPaymentSys);
                handleClickCheckboxPaymentSys(null, newSelectedPaymentSys);
                return;
            }
            setSelectedPaymentSys(newSelectedPaymentSys);
            if (isActivePaymentSys) {
                setSelectedPaymentSystems((prev) => {
                    if (isComboPayment) {
                        const prevItem = prev.find((item) => item.keyType === key);
                        return prevItem
                            ? [
                                  ...prev.filter((item) => item.keyType !== key),
                                  { ...newSelectedPaymentSys, sumValue: prevItem?.sumValue }
                              ]
                            : [...prev, { ...selectedPaymentSys, keyType: key }];
                    }
                    if (selectedPaymentSys) {
                        return [{ ...newSelectedPaymentSys, sumValue: cartData?.total_price }];
                    }
                    return [...prev];
                });
            }
        },
        [
            cartData?.total_price,
            handleClickCheckboxPaymentSys,
            isActivePaymentSys,
            isComboPayment,
            selectedPaymentSys,
            setSelectedPaymentSystems,
            settings
        ]
    );

    const setCurrency = useCallback(
        (key, val) => {
            setSelectedPaymentSystems((prev) => {
                return [
                    ...prev.map((item) => {
                        if (item.keyType === PAYMENT_SYSTEM_TYPE) {
                            return { ...item, selectedCurrency: val };
                        }
                        return item;
                    })
                ];
            });
            setSelectedPaymentSys((prev) => {
                return { ...prev, selectedCurrency: val };
            });
            setCurrencyValue(val);
        },
        [setSelectedPaymentSystems]
    );

    return {
        selectedPaymentSys,
        setSelectedPaymentSys,
        currencyValue,
        setCurrencyValue,
        handleSetSelectedPaymentSys,
        currentCurrenciesList,
        setCurrency,
        handleClickCheckboxInternalAcc,
        handleClickOnPaymentSys,
        handleClickCheckboxPaymentSys
    };
};
